// ─── Shared constants ─────────────────────────────────────────────────────────

export const SLICE_COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#84CC16",
  "#10B981",
  "#06B6D4",
  "#3B82F6",
  "#8B5CF6",
];

export const DISPLAYED_SLICES_OPTIONS = [5, 6, 7, 8];

export const TIMEZONE = "America/New_York";

export const DEFAULT_SLICE = {
  productSearch: "",
  discount: "",
  price: null,
  productId: null,
  variantId: null,
  productName: "",
  variantName: "",
  businessId: null,
};

export const isZeroPricedSlice = (slice) => {
  if (slice?.price === null || slice?.price === undefined || slice?.price === "") {
    return false;
  }

  return Number(slice.price) === 0;
};

const requiresDiscount = (slice, rewardType) =>
  rewardType === "Discount" && !isZeroPricedSlice(slice);

// ─── Shared utilities ─────────────────────────────────────────────────────────

/** Returns an array of `count` empty slice objects. */
export const createInitialSlices = (count) =>
  Array.from({ length: count }, () => ({ ...DEFAULT_SLICE }));

/**
 * Detects the unique product pattern at the start of the slices array.
 * Stops at the first empty slot OR when a productId repeats (cycle detected).
 */
export const detectPattern = (slices, count) => {
  const pattern = [];
  const seenIds = new Set();
  for (let i = 0; i < count; i++) {
    const slice = slices[i];
    if (!slice?.productId) break;
    if (seenIds.has(slice.productId)) break;
    pattern.push({ ...slice });
    seenIds.add(slice.productId);
  }
  return pattern;
};

/** Gets the next Sunday (inclusive — if already sunday adds 7 days). */
export const getNextSunday = (date) => {
  if (!date) return null;
  const next = new Date(date);
  const daysUntilSunday = (7 - next.getDay()) % 7 || 7;
  next.setDate(next.getDate() + daysUntilSunday);
  return next;
};

/** Formats a Date to "YYYY-MM-DDTHH:mm:ss.sssZ" without timezone offset. */
export const formatDateToISO = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

/** Filter function for react-datepicker — only allows Mondays. */
export const filterMondaysOnly = (date) => date.getDay() === 1;

/** Parses a raw date string/timestamp to a "YYYY-MM-DD" string for input[type=date]. */
export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const date = new Date(
    typeof dateString === "string" && /^\d+$/.test(dateString)
      ? parseInt(dateString)
      : dateString,
  );
  return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

/** Formats a date range like "Apr 7 – Apr 13, 2026". */
export const formatDateRange = (startDate, endDate) => {
  try {
    const parse = (d) =>
      new Date(
        typeof d === "string" && /^\d+$/.test(d) ? parseInt(d) : d,
      );
    const start = parse(startDate);
    const end = parse(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "Invalid Date Range";
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
  } catch {
    return "Invalid Date Range";
  }
};

/**
 * Builds a SliceInput object for the GraphQL mutation from a flat slice.
 * @param {object} slice - flat slice with productId, productName, etc.
 * @param {string} rewardType - "Discount" | "FreeProduct"
 * @param {string|null} fallbackBusinessId - businessId from store if slice lacks one
 */
export const buildSliceInput = (slice, rewardType, fallbackBusinessId) => {
  const input = {
    businessId: slice.businessId || fallbackBusinessId,
    productId: slice.productId,
    productName: slice.productName,
  };
  if (slice.variantId) {
    input.variantId = slice.variantId;
    input.variantName = slice.variantName;
  }
  if (rewardType === "Discount") {
    input.discount = parseFloat(slice.discount) || 0;
  }
  return input;
};

/**
 * Given slices + duplicate flag, returns the slices array to send to the backend.
 * When duplicateSlice is true, returns only the unique pattern products.
 */
export const buildSlicesPayload = (slices, displayedSlices, duplicateSlice) => {
  const activeSlices = slices.slice(0, displayedSlices);
  if (!duplicateSlice) return activeSlices;

  const seen = new Set();
  const pattern = [];
  for (const slice of activeSlices) {
    if (!slice.productId) break;
    if (seen.has(slice.productId)) break;
    pattern.push(slice);
    seen.add(slice.productId);
  }
  return pattern;
};

/**
 * Validates the slices portion of the form.
 * Returns an object:  { sliceErrors: {}, duplicateNoProduct: string | null }
 */
export const validateSlices = (slices, displayedSlices, duplicateSlice, rewardType) => {
  const sliceErrors = {};
  let duplicateNoProduct = null;

  if (duplicateSlice) {
    const pattern = detectPattern(slices, displayedSlices);
    if (pattern.length === 0) {
      duplicateNoProduct = "Please add at least one product before enabling duplicate.";
    } else {
      pattern.forEach((slice, i) => {
        if (
          requiresDiscount(slice, rewardType) &&
          (!slice.discount || Number(slice.discount) <= 0 || Number(slice.discount) > 100)
        ) {
          sliceErrors[i] = { discount: "1-100%" };
        }
      });
    }
  } else {
    slices.slice(0, displayedSlices).forEach((slice, i) => {
      const errs = {};
      if (!slice.productSearch?.trim()) errs.productSearch = "Product is required";
      if (
        requiresDiscount(slice, rewardType) &&
        (!slice.discount || Number(slice.discount) <= 0 || Number(slice.discount) > 100)
      ) {
        errs.discount = "1-100%";
      }
      if (Object.keys(errs).length > 0) sliceErrors[i] = errs;
    });
  }

  return { sliceErrors, duplicateNoProduct };
};

/**
 * Quick boolean check for the Publish/Update button enable state.
 */
export const isSlicesValid = (slices, displayedSlices, duplicateSlice, rewardType) => {
  const activeSlices = slices.slice(0, displayedSlices);
  if (duplicateSlice) {
    const pattern = detectPattern(activeSlices, displayedSlices);
    if (pattern.length === 0) return false;
    return pattern.every(
      (s) =>
        !requiresDiscount(s, rewardType) ||
        (s.discount && Number(s.discount) > 0 && Number(s.discount) <= 100),
    );
  }
  return activeSlices.every(
    (s) =>
      s.productSearch?.trim() &&
      (!requiresDiscount(s, rewardType) ||
        (s.discount && Number(s.discount) > 0 && Number(s.discount) <= 100)),
  );
};

/**
 * Formats GraphQL validation error messages into user-friendly messages.
 * 
 * DEVELOPER NOTE: If you need to update error message formatting, modify the logic
 * below to match your desired error message format. This function handles parsing
 * validation errors from the server response (e.g., "slices[0].discount" errors).
 * 
 * Called from:
 * - RewardWheelCreatePage.jsx (onError handler in CREATE_REWARD_WHEEL mutation)
 * - RewardWheelDetailPage.jsx (onError handler in UPDATE_REWARD_WHEEL mutation)
 * 
 * @param {string} errorMessage - Raw error message from GraphQL response
 * @returns {string} - User-friendly formatted error message
 */
export const formatValidationError = (errorMessage) => {
  if (!errorMessage) return "Something went wrong, please try again.";

  // Pattern: "slices[1].discount" must be greater than or equal to 1
  // Extract slice index and field from error message
  const discountErrorRegex = /slices\[(\d+)\]\.discount.*must be greater than or equal to 1/gi;
  const productErrorRegex = /slices\[(\d+)\]\.productSearch.*required/gi;

  // Check for discount errors and format them
  const discountMatches = [...errorMessage.matchAll(discountErrorRegex)];
  if (discountMatches.length > 0) {
    const sliceNumbers = discountMatches.map((match) => Number(match[1]) + 1); // Convert to 1-based index
    return `Please enter discount amount for Slice ${sliceNumbers.join(", ")}.`;
  }

  // Check for product errors and format them
  const productMatches = [...errorMessage.matchAll(productErrorRegex)];
  if (productMatches.length > 0) {
    const sliceNumbers = productMatches.map((match) => Number(match[1]) + 1); // Convert to 1-based index
    return `Please select a product for Slice ${sliceNumbers.join(", ")}.`;
  }

  // If it's a validation error with "slices" keyword, return generic message
  if (errorMessage.includes("slices")) {
    return "Please check the slice configuration and try again.";
  }

  // Return original error message if no pattern matches
  return errorMessage;
};
